import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Link2, Loader2, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { formatDateTime, parseApiDate } from '@/lib/format';
import { StatusBadge } from './status-badge';
import {
  useApprovalLink,
  useApproveRequest,
  useRejectRequest,
  useUpdateRequest,
} from './account-requests.api';
import {
  GENDER,
  LANGUAGE,
  fullName,
  isSettled,
  statusOf,
  type AccountRequest,
  type AccountRequestUpdate,
} from './types';

/**
 * One request: what was asked for, and the three things support can do about it.
 *
 * Departures from the original drawer, all of them bugs rather than taste:
 *
 *  - Two inputs were disabled by their own value
 *    (`disabled={loading || requestDetails?.businessLicense}`), so a business
 *    licence or a referral code became uneditable the moment it held one
 *    character — including the character just typed into it.
 *  - Rejecting asked "are you sure?" in a popconfirm and then opened a second
 *    modal that asked again and carried the message box. One dialog, which is
 *    where the optional message belongs.
 *  - Update posted the whole DTO back, `createdAt` and `associatedUser`
 *    included. It sends the sixteen fields the endpoint documents.
 */

/** The editable half of a request, in the shape the update endpoint wants. */
function toDraft(request: AccountRequest): AccountRequestUpdate {
  return {
    id: request.id,
    businessName: request.businessName ?? '',
    firstName: request.firstName ?? '',
    lastName: request.lastName ?? '',
    email: request.email ?? '',
    gender: request.gender ?? GENDER.notSaid,
    language: request.language ?? LANGUAGE.english,
    phoneNumber: request.phoneNumber ?? '',
    addressLine1: request.addressLine1 ?? '',
    addressLine2: request.addressLine2 ?? '',
    city: request.city ?? '',
    state: request.state ?? '',
    country: request.country ?? '',
    externalClientId: request.externalClientId ?? '',
    businessLicense: request.businessLicense ?? '',
    friendCode: request.friendCode ?? '',
  };
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}

export function RequestDetailSheet({
  request,
  open,
  onOpenChange,
}: {
  request: AccountRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<AccountRequestUpdate | null>(null);
  /** What is on the server, so Save can tell whether anything actually moved. */
  const [baseline, setBaseline] = useState<AccountRequestUpdate | null>(null);
  const [rejectionMessage, setRejectionMessage] = useState('');

  const approve = useApproveRequest();
  const reject = useRejectRequest();
  const update = useUpdateRequest();
  const link = useApprovalLink();

  const requestId = request?.id;

  // A different request is a different form. In the original the rejection
  // message was cleared here but the draft was not re-seeded until the next
  // render, so reopening the drawer briefly showed the previous applicant.
  useEffect(() => {
    const next = request ? toDraft(request) : null;
    setDraft(next);
    setBaseline(next);
    setRejectionMessage('');
    link.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const status = request ? statusOf(request) : 'pending';
  const settled = !!request && isSettled(request);
  const busy =
    approve.isPending || reject.isPending || update.isPending || link.isPending;
  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(baseline);

  const set = <K extends keyof AccountRequestUpdate>(
    key: K,
    value: AccountRequestUpdate[K]
  ) => setDraft((current) => (current ? { ...current, [key]: value } : current));

  const failed = () => toast.error(t('accountRequests.actionFailed'));
  const who = request
    ? fullName(request) || request.email || ''
    : '';

  function runApprove() {
    approve.mutate(requestId!, {
      onSuccess: () => {
        toast.success(t('accountRequests.approvedToast'));
        onOpenChange(false);
      },
      onError: failed,
    });
  }

  function runReject() {
    reject.mutate(
      { id: requestId!, message: rejectionMessage.trim() },
      {
        onSuccess: () => {
          toast.success(t('accountRequests.rejectedToast'));
          onOpenChange(false);
        },
        onError: failed,
      }
    );
  }

  function copyLink() {
    link.mutate(requestId!, {
      onSuccess: async (url) => {
        try {
          await navigator.clipboard.writeText(url);
          toast.success(t('accountRequests.linkCopied'));
        } catch {
          // Clipboard access is denied outside a secure context. Showing the
          // link beats a button that silently does nothing.
          toast.info(url);
        }
      },
      onError: failed,
    });
  }

  function save() {
    const values = draft!;
    update.mutate(values, {
      onSuccess: () => {
        toast.success(t('accountRequests.saved'));
        // The drawer stays open, so what was just saved becomes the new
        // baseline rather than leaving Save enabled against nothing.
        setBaseline(values);
      },
      onError: failed,
    });
  }

  const linkExpiry = parseApiDate(request?.linkExpirationDate);
  const linkExpired = !!linkExpiry && linkExpiry.getTime() < Date.now();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle className="flex items-center gap-2">
            <span className="truncate">
              {who || t('accountRequests.requestDetails')}
            </span>
            {request && <StatusBadge status={status} />}
          </SheetTitle>
          {request && (
            <p className="text-muted-foreground text-xs">
              {t('accountRequests.requestedOn')}:{' '}
              {formatDateTime(request.createdAt)}
              {linkExpiry && (
                <>
                  {' · '}
                  {t(
                    linkExpired
                      ? 'accountRequests.linkExpiredOn'
                      : 'accountRequests.linkExpiresOn',
                    { date: formatDateTime(linkExpiry) }
                  )}
                </>
              )}
            </p>
          )}
        </SheetHeader>

        {draft && (
          <div className="space-y-5 p-4">
            {settled ? (
              <p className="bg-muted text-muted-foreground rounded-xl p-3 text-sm">
                {t('accountRequests.alreadyRegistered')}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" disabled={busy}>
                      {approve.isPending ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        <Check />
                      )}
                      {t('accountRequests.approve')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('accountRequests.approve')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('accountRequests.approveConfirm', { name: who })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {t('common.cancel')}
                      </AlertDialogCancel>
                      <AlertDialogAction onClick={runApprove}>
                        {t('accountRequests.approve')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" disabled={busy}>
                      {reject.isPending ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        <X />
                      )}
                      {t('accountRequests.reject')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('accountRequests.reject')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('accountRequests.rejectConfirm', { name: who })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="rejection-message"
                        className="text-xs font-semibold"
                      >
                        {t('accountRequests.rejectionMessage')}
                      </Label>
                      <Textarea
                        id="rejection-message"
                        rows={3}
                        value={rejectionMessage}
                        onChange={(event) =>
                          setRejectionMessage(event.target.value)
                        }
                        placeholder={t('accountRequests.rejectionPlaceholder')}
                      />
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {t('common.cancel')}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={runReject}
                      >
                        {t('accountRequests.reject')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                {/* The link only exists once the request has been approved. */}
                {status === 'approved' && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={copyLink}
                  >
                    {link.isPending ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <Link2 />
                    )}
                    {t('accountRequests.getApprovalLink')}
                  </Button>
                )}
              </div>
            )}

            <div className="space-y-4">
              <Field
                label={t('accountRequests.businessName')}
                htmlFor="businessName"
              >
                <Input
                  id="businessName"
                  value={draft.businessName}
                  disabled={settled || busy}
                  onChange={(e) => set('businessName', e.target.value)}
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={t('accountRequests.firstName')}
                  htmlFor="firstName"
                >
                  <Input
                    id="firstName"
                    value={draft.firstName}
                    disabled={settled || busy}
                    onChange={(e) => set('firstName', e.target.value)}
                  />
                </Field>
                <Field label={t('accountRequests.lastName')} htmlFor="lastName">
                  <Input
                    id="lastName"
                    value={draft.lastName}
                    disabled={settled || busy}
                    onChange={(e) => set('lastName', e.target.value)}
                  />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('accountRequests.email')} htmlFor="email">
                  <Input
                    id="email"
                    type="email"
                    value={draft.email}
                    disabled={settled || busy}
                    onChange={(e) => set('email', e.target.value)}
                  />
                </Field>
                <Field label={t('accountRequests.phone')} htmlFor="phoneNumber">
                  <Input
                    id="phoneNumber"
                    type="tel"
                    value={draft.phoneNumber}
                    disabled={settled || busy}
                    onChange={(e) => set('phoneNumber', e.target.value)}
                  />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('accountRequests.gender')} htmlFor="gender">
                  <Select
                    value={String(draft.gender)}
                    disabled={settled || busy}
                    onValueChange={(value) => set('gender', Number(value))}
                  >
                    <SelectTrigger id="gender" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={String(GENDER.male)}>
                        {t('accountRequests.male')}
                      </SelectItem>
                      <SelectItem value={String(GENDER.female)}>
                        {t('accountRequests.female')}
                      </SelectItem>
                      <SelectItem value={String(GENDER.notSaid)}>
                        {t('accountRequests.notSaid')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t('common.language')} htmlFor="language">
                  <Select
                    value={String(draft.language)}
                    disabled={settled || busy}
                    onValueChange={(value) => set('language', Number(value))}
                  >
                    <SelectTrigger id="language" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={String(LANGUAGE.english)}>
                        {t('common.english')}
                      </SelectItem>
                      <SelectItem value={String(LANGUAGE.spanish)}>
                        {t('common.spanish')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field
                label={t('accountRequests.addressLine1')}
                htmlFor="addressLine1"
              >
                <Input
                  id="addressLine1"
                  value={draft.addressLine1}
                  disabled={settled || busy}
                  onChange={(e) => set('addressLine1', e.target.value)}
                />
              </Field>
              <Field
                label={t('accountRequests.addressLine2')}
                htmlFor="addressLine2"
              >
                <Input
                  id="addressLine2"
                  value={draft.addressLine2}
                  disabled={settled || busy}
                  onChange={(e) => set('addressLine2', e.target.value)}
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-3">
                <Field label={t('accountRequests.city')} htmlFor="city">
                  <Input
                    id="city"
                    value={draft.city}
                    disabled={settled || busy}
                    onChange={(e) => set('city', e.target.value)}
                  />
                </Field>
                <Field label={t('accountRequests.state')} htmlFor="state">
                  <Input
                    id="state"
                    value={draft.state}
                    disabled={settled || busy}
                    onChange={(e) => set('state', e.target.value)}
                  />
                </Field>
                <Field label={t('accountRequests.country')} htmlFor="country">
                  <Input
                    id="country"
                    value={draft.country}
                    disabled={settled || busy}
                    onChange={(e) => set('country', e.target.value)}
                  />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={t('accountRequests.externalClientId')}
                  htmlFor="externalClientId"
                >
                  <Input
                    id="externalClientId"
                    value={draft.externalClientId}
                    disabled={settled || busy}
                    onChange={(e) => set('externalClientId', e.target.value)}
                  />
                </Field>
                <Field
                  label={t('accountRequests.businessLicense')}
                  htmlFor="businessLicense"
                >
                  <Input
                    id="businessLicense"
                    value={draft.businessLicense}
                    disabled={settled || busy}
                    onChange={(e) => set('businessLicense', e.target.value)}
                  />
                </Field>
              </div>

              <Field
                label={t('accountRequests.referralCode')}
                htmlFor="friendCode"
              >
                <Input
                  id="friendCode"
                  value={draft.friendCode}
                  disabled={settled || busy}
                  onChange={(e) => set('friendCode', e.target.value)}
                />
              </Field>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => onOpenChange(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button disabled={settled || busy || !dirty} onClick={save}>
                {update.isPending && <Loader2 className="animate-spin" />}
                {t('common.save')}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
